import { NATIVE_APP_STORE_URLS, type NativeAppPromotionRuntime } from "./nativeAppPromotion";
import type { WebCampaignAttribution } from "./analytics/webCampaignAttribution";

export const DEFAULT_APPSFLYER_WEB_POPUP_ONELINK_URL =
  "https://mypetroutine-web-popup.onelink.me/MXBC/4own6fd1";
export const DEFAULT_APPSFLYER_WEB_POPUP_MEDIA_SOURCE = "mpr_web_popup";
export const APPSFLYER_WEB_POPUP_PATH = "/MXBC/4own6fd1";
export const APPSFLYER_WEB_POPUP_HOST = "mypetroutine-web-popup.onelink.me";

export type NativeAppAttributionLink = {
  storePlatform: "android" | "ios";
  url: string;
  linkType: "appsflyer_onelink" | "static_store_fallback";
  campaignKey: string;
  mediaSource: string | null;
};

export type AppsFlyerPopupConfig = {
  oneLinkUrl: string;
  mediaSource: string;
};

export function resolveAppsFlyerPopupConfig(overrides: Partial<AppsFlyerPopupConfig> = {}): AppsFlyerPopupConfig {
  return {
    oneLinkUrl: overrides.oneLinkUrl?.trim() || DEFAULT_APPSFLYER_WEB_POPUP_ONELINK_URL,
    mediaSource: overrides.mediaSource?.trim() || DEFAULT_APPSFLYER_WEB_POPUP_MEDIA_SOURCE,
  };
}

export function validateAppsFlyerPopupConfig(config: AppsFlyerPopupConfig) {
  try {
    const url = new URL(config.oneLinkUrl);
    return url.protocol === "https:"
      && url.hostname === APPSFLYER_WEB_POPUP_HOST
      && url.pathname === APPSFLYER_WEB_POPUP_PATH
      && !url.username
      && !url.password
      && !url.search
      && !url.hash
      && url.toString() === DEFAULT_APPSFLYER_WEB_POPUP_ONELINK_URL
      && config.mediaSource === DEFAULT_APPSFLYER_WEB_POPUP_MEDIA_SOURCE;
  } catch {
    return false;
  }
}

function safeAppsFlyerField(value: string | null | undefined, maxLength: number) {
  if (!value || value.length > maxLength || !/^[A-Za-z0-9 _.-]+$/u.test(value)) return null;
  return value;
}

function getCampaignKey(attribution: WebCampaignAttribution | null) {
  return attribution?.campaign || attribution?.id || "web_unknown";
}

function getChannel(attribution: WebCampaignAttribution | null) {
  if (attribution?.source === "ig") return "Instagram";
  return null;
}

function staticFallback(storePlatform: "android" | "ios", attribution: WebCampaignAttribution | null): NativeAppAttributionLink {
  return {
    storePlatform,
    url: NATIVE_APP_STORE_URLS[storePlatform],
    linkType: "static_store_fallback",
    campaignKey: getCampaignKey(attribution),
    mediaSource: null,
  };
}

export function buildNativeAppAttributionLink(input: {
  runtime: NativeAppPromotionRuntime;
  storePlatform: "android" | "ios";
  attribution: WebCampaignAttribution | null;
  oneLinkUrl: string;
  mediaSource: string;
}): NativeAppAttributionLink {
  const fallback = () => staticFallback(input.storePlatform, input.attribution);
  const expectedPlatform = input.runtime.isAndroid ? "android" : input.runtime.isIos ? "ios" : null;
  const config = { oneLinkUrl: input.oneLinkUrl, mediaSource: input.mediaSource };
  if (expectedPlatform !== input.storePlatform || !validateAppsFlyerPopupConfig(config)) return fallback();

  const url = new URL(config.oneLinkUrl);
  const existingKeys = [...url.searchParams.keys()].map((key) => key.toLowerCase());
  const runtimeOwned = new Set(["pid", "c", "af_channel", "af_ad"]);
  const rawClickIds = new Set(["fbclid", "gclid", "gbraid", "wbraid"]);
  if (existingKeys.some((key) => runtimeOwned.has(key) || rawClickIds.has(key))) return fallback();

  const campaignKey = safeAppsFlyerField(getCampaignKey(input.attribution), 100) ?? "web_unknown";
  url.searchParams.set("c", campaignKey);
  const channel = safeAppsFlyerField(getChannel(input.attribution), 20);
  const ad = safeAppsFlyerField(input.attribution?.content, 100);
  if (channel) url.searchParams.set("af_channel", channel);
  if (ad) url.searchParams.set("af_ad", ad);
  if (url.toString().length > 2000) return fallback();

  return {
    storePlatform: input.storePlatform,
    url: url.toString(),
    linkType: "appsflyer_onelink",
    campaignKey,
    mediaSource: config.mediaSource,
  };
}

export function getNativeAppStaticStoreLink(storePlatform: "android" | "ios") {
  return NATIVE_APP_STORE_URLS[storePlatform];
}
